/**
 * How a county name is written out loud, once. The browser's copy.
 *
 * THIS FILE IS A DELIBERATE SECOND COPY of server/lib/countyDisplay.js, which
 * carries the full reasoning. The short version: calendar_counties.county_name
 * stores "Marinette County", so a screen that appends the word again renders
 * "Marinette County County, WI", which is what a paying subscriber saw on their
 * first login. The same column also holds "Acadia Parish", "Bethel Census Area"
 * and "Carson City", so the rule is to append only when the stored name carries
 * no suffix of its own.
 *
 * WHY A COPY AND NOT AN IMPORT. Create React App refuses any import from
 * outside client/src, so the browser cannot reach server/lib. Parity is kept by
 * scripts/check-calendar.js instead: it evaluates this file against the same
 * fixture table as the server copy and fails if either answers differently, and
 * it compares the suffix vocabulary itself. That is the shape check:settings
 * already uses for the ConfigContext literal.
 *
 * KEEP THIS FILE FREE OF IMPORTS. The check reads it as text and evaluates it
 * in isolation, which is the only way to prove behaviour rather than compare
 * two regular expressions and hope.
 */

export const COUNTY_SUFFIXES = [
  'county',
  'parish',
  'borough',
  'census area',
  'city and borough',
  'municipality',
  'city'
];

const SUFFIX_PATTERN = new RegExp(
  `\\s(${COUNTY_SUFFIXES.map(s => s.replace(/ /g, '\\s+')).join('|')})\\s*$`,
  'i'
);

/** True when the stored name already says what kind of place it is. */
export function hasCountySuffix(name) {
  return SUFFIX_PATTERN.test(String(name == null ? '' : name).trim());
}

/**
 * The county on its own: "Marinette County", "Acadia Parish", "Carson City".
 *
 * An empty name returns an empty string rather than the bare word "County",
 * because a label reading "County, WI" is worse than a blank.
 */
export function countyLabel(name) {
  const trimmed = String(name == null ? '' : name).trim();
  if (trimmed === '') return '';
  return hasCountySuffix(trimmed) ? trimmed : `${trimmed} County`;
}

/** The county and its state: "Marinette County, WI". */
export function countyWithState(name, stateCode) {
  const county = countyLabel(name);
  const code = String(stateCode == null ? '' : stateCode).trim().toUpperCase();
  if (county === '') return code;
  if (code === '') return county;
  return `${county}, ${code}`;
}
